import React, { useEffect, useRef, useState } from "react";

function stringifyLog(args) {
  return args
    .map((arg) => {
      if (typeof arg === "string") return arg;

      try {
        return JSON.stringify(arg, null, 2);
      } catch {
        return String(arg);
      }
    })
    .join(" ");
}

export function WebViewConsole({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("log");

  const idRef = useRef(0);
  const bottomRef = useRef(null);

  useEffect(() => {
    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
    };

    const pushLog = (level, args) => {
      const item = {
        id: idRef.current++,
        level,
        time: new Date().toLocaleTimeString(),
        message: stringifyLog(args),
      };

      // 缓存数量保持 300 条
      setLogs((prev) => [...prev.slice(-300), item]);
    };

    console.log = (...args) => {
      originalConsole.log(...args);
      pushLog("log", args);
    };

    console.warn = (...args) => {
      originalConsole.warn(...args);
      pushLog("warn", args);
    };

    console.error = (...args) => {
      originalConsole.error(...args);
      pushLog("error", args);
    };

    const handleError = (event) => {
      pushLog("error", [
        event.message,
        `${event.filename}:${event.lineno}:${event.colno}`,
      ]);
    };

    const handleUnhandledRejection = (event) => {
      pushLog("error", ["Unhandled Promise Rejection", event.reason]);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      console.log = originalConsole.log;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;

      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  const currentLogs = logs.filter((item) => item.level === activeTab);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentLogs.length, activeTab]);

  const getColor = (level) => {
    switch (level) {
      case "error":
        return "#ff6b6b";
      case "warn":
        return "#ffd166";
      default:
        return "#d0d0d0";
    }
  };

  const countByLevel = {
    log: logs.filter((item) => item.level === "log").length,
    warn: logs.filter((item) => item.level === "warn").length,
    error: logs.filter((item) => item.level === "error").length,
  };

  const tabStyle = (tab) => ({
    flex: 1,
    height: 34,
    padding: 0,
    background: activeTab === tab ? "#333" : "#1f1f1f",
    color: activeTab === tab ? "#fff" : "#aaa",
    border: "none",
    borderBottom:
      activeTab === tab ? "2px solid #4dabf7" : "2px solid transparent",
    fontSize: 12,
    boxSizing: "border-box",
    pointerEvents: "auto",
  });

  const buttonStyle = {
    height: 28,
    padding: "0 10px",
    color: "#fff",
    borderRadius: 6,
    fontSize: 12,
    cursor: "pointer",
    boxSizing: "border-box",
    pointerEvents: "auto",
  };

  return (
    <div
      style={{
        position: "fixed",
        left: 8,
        right: 8,
        bottom: 8,

        // 显示区域加大
        height: "65vh",

        zIndex: 999999,
        background: "rgba(0,0,0,0.72)",
        color: "#fff",
        fontSize: 12,
        fontFamily: "monospace",
        borderRadius: 8,
        border: "1px solid #444",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",

        // 控制台主体不拦截 WebGL 点击
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          height: 44,
          minHeight: 44,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 12px",
          background: "#111",
          borderBottom: "1px solid #444",
          boxSizing: "border-box",
          flexShrink: 0,
          pointerEvents: "none",
        }}
      >
        <span
          style={{
            color: "#fff",
            fontSize: 12,
            lineHeight: "44px",
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          WebView Console
        </span>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            height: 44,
            pointerEvents: "auto",
          }}
        >
          <button
            onClick={() => setLogs([])}
            style={{
              ...buttonStyle,
              background: "#222",
              border: "1px solid #555",
            }}
          >
            Clear
          </button>

          <button
            onClick={onClose}
            style={{
              ...buttonStyle,
              background: "#b91c1c",
              border: "1px solid #ef4444",
            }}
          >
            Close
          </button>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          height: 34,
          minHeight: 34,
          flexShrink: 0,
          pointerEvents: "auto",
        }}
      >
        <button style={tabStyle("log")} onClick={() => setActiveTab("log")}>
          Log ({countByLevel.log})
        </button>

        <button style={tabStyle("warn")} onClick={() => setActiveTab("warn")}>
          Warn ({countByLevel.warn})
        </button>

        <button style={tabStyle("error")} onClick={() => setActiveTab("error")}>
          Error ({countByLevel.error})
        </button>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 8,
          WebkitOverflowScrolling: "touch",
          boxSizing: "border-box",

          // 日志区域不拦截 WebGL 点击
          pointerEvents: "none",
        }}
      >
        {currentLogs.length === 0 ? (
          <div style={{ color: "#777", padding: 8 }}>No {activeTab} logs</div>
        ) : (
          currentLogs.map((log) => (
            <div
              key={log.id}
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: getColor(log.level),
                marginBottom: 6,
                pointerEvents: "none",
              }}
            >
              <span style={{ opacity: 0.6 }}>[{log.time}]</span>{" "}
              <span>[{log.level}]</span> {log.message}
            </div>
          ))
        )}

        <div ref={bottomRef} />
      </div>
    </div>
  );
}